import { EventCardAttendee } from "#webapp/src/components/event-card/types.js";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect } from "react";
import { SearchEvent } from "../../../hooks/useEventsSearch";
import { EventSelectOption } from "./EventSelect";
import { PersonSelectOption } from "./PersonSelect";
import { useKeyboardSelect } from "./hooks/useKeyboardSelect";

type Props = {
  eventOptions: SearchEvent[];
  inputRef: React.RefObject<HTMLDivElement>;
  onSelectEvent: (person: SearchEvent, fromMouseEvent: boolean) => void;
  onSelectPerson: (person: EventCardAttendee, fromMouseEvent: boolean) => void;
  personOptions: EventCardAttendee[];
};

export const ComboSelect = ({
  eventOptions,
  inputRef,
  onSelectEvent,
  onSelectPerson,
  personOptions,
}: Props) => {
  const handleSelect = useCallback(
    (index: number, fromMouseEvent: boolean) => {
      if (index < personOptions.length) {
        onSelectPerson(personOptions[index], fromMouseEvent);
      } else {
        onSelectEvent(eventOptions[index - personOptions.length], fromMouseEvent);
      }
    },
    [eventOptions, onSelectEvent, onSelectPerson, personOptions],
  );

  const isActive = !isEmpty(personOptions) || !isEmpty(eventOptions);

  const onKeyboardSelect = useCallback((index: number) => handleSelect(index, false), [
    handleSelect,
  ]);

  const onMouseSelect = useCallback(
    (index: number) => {
      handleSelect(index, true);
    },
    [handleSelect],
  );

  const { activeIndex, setActiveIndex } = useKeyboardSelect({
    active: isActive,
    inputRef,
    onSelect: onKeyboardSelect,
    optionCount: eventOptions.length + personOptions.length,
  });

  useEffect(() => {
    // Reset index to top on search change
    setActiveIndex(0);
  }, [eventOptions, personOptions, setActiveIndex]);

  return (
    <>
      {!isEmpty(personOptions) && (
        <div className="cw-caption cw-font-bold cw-my-1 cw-text-muted cw-px-1">Users</div>
      )}
      {personOptions.map((option, index) => (
        <PersonSelectOption
          highlight={index === activeIndex}
          key={`${option.primaryCalendar}-${index}`}
          onMouseDown={() => onMouseSelect(index)}
          onMouseEnter={() => setActiveIndex(index)}
          option={option}
        />
      ))}
      {!isEmpty(eventOptions) && (
        <div className="cw-caption cw-font-bold cw-my-1 cw-text-muted cw-px-1">Events</div>
      )}
      {eventOptions.map((option, index) => (
        <EventSelectOption
          highlight={activeIndex === index + personOptions.length}
          key={`${option.externalEventId}-${index}`}
          onMouseDown={() => onMouseSelect(index + personOptions.length)}
          onMouseEnter={() => setActiveIndex(index + personOptions.length)}
          option={option}
        />
      ))}
    </>
  );
};
